import React from 'react';
import { Text, Select, type SelectOption } from '@chatsdet/ui';
import {
  useOrgCredentialTemplates,
  type CredentialTemplate,
} from '../../../hooks/useCredentialTemplates';
import { useOrgCredentials, type OrgCredential } from '../../../hooks/useOrgCredentials';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';

/**
 * Shared "credential or persona" picker used by both the login node and the
 * api_sequence step editor. A persona (credential template) resolves to a
 * concrete credential per-environment at run time — the only way to authenticate
 * portably against a fresh sandbox — so both node types must be able to select
 * one. Previously this lived inline in `LoginNodeConfig`; it's extracted here so
 * the api_sequence step (`SortableStepCard`) reuses the exact same option list,
 * "Persona" tagging, and select/clear semantics rather than reimplementing them.
 */

export type CredentialAndPersonaOptions = {
  templates: CredentialTemplate[];
  credentials: OrgCredential[];
  propertyNameById: Map<string, string>;
  templateNameById: Map<string, string>;
  templateIdSet: Set<string>;
  isLoading: boolean;
};

/**
 * Load + sort org credential templates (personas) and credentials, optionally
 * scoped to a single property. Lifted verbatim from `LoginNodeConfig`; the only
 * addition is the optional `propertyId` filter (the api_sequence editors scope
 * their credential list to the step's property, the login node does not).
 */
export function useCredentialAndPersonaOptions(
  propertyId?: string | null
): CredentialAndPersonaOptions {
  const templatesQ = useOrgCredentialTemplates();
  const credentialsQ = useOrgCredentials();
  const propertiesQ = usePropertySummaries();

  const propertyNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of propertiesQ.data ?? []) m.set(p.id, p.name);
    return m;
  }, [propertiesQ.data]);

  const templates = React.useMemo(
    () =>
      [...(templatesQ.data ?? [])]
        .filter((t) => !propertyId || t.propertyId === propertyId)
        .sort((a, b) => {
          const ap = propertyNameById.get(a.propertyId) ?? '';
          const bp = propertyNameById.get(b.propertyId) ?? '';
          if (ap !== bp) return ap.localeCompare(bp);
          return a.name.localeCompare(b.name);
        }),
    [templatesQ.data, propertyNameById, propertyId]
  );

  const credentials = React.useMemo(() => {
    return [...(credentialsQ.credentials ?? [])]
      .filter((c) => !propertyId || c.propertyId === propertyId)
      .sort((a, b) => {
        const ap = a.propertyName ?? '';
        const bp = b.propertyName ?? '';
        if (ap !== bp) return ap.localeCompare(bp);
        const aBound = a.templateId ? 0 : 1;
        const bBound = b.templateId ? 0 : 1;
        if (aBound !== bBound) return aBound - bBound;
        return a.name.localeCompare(b.name);
      });
  }, [credentialsQ.credentials, propertyId]);

  const templateNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const t of templates) m.set(t.id, t.name);
    return m;
  }, [templates]);

  const templateIdSet = React.useMemo(() => new Set(templates.map((t) => t.id)), [templates]);

  return {
    templates,
    credentials,
    propertyNameById,
    templateNameById,
    templateIdSet,
    isLoading: templatesQ.isLoading || credentialsQ.isLoading,
  };
}

type CredentialPersonaPickerProps = {
  templates: CredentialTemplate[];
  credentials: OrgCredential[];
  propertyNameById: Map<string, string>;
  templateNameById: Map<string, string>;
  isLoading: boolean;
  selectedTemplateId: string | null;
  selectedCredentialId: string | null;
  /** Receives the picked id (a template id or credential id), or null to clear. */
  onPick: (id: string | null) => void;
  label?: string | null;
  ariaLabel?: string;
  /** Prepend a "No credential (public)" clear option (api_sequence steps allow unauthenticated calls). */
  allowNoCredential?: boolean;
  /**
   * Restrict the option list to concrete credentials — omit personas
   * (credential templates) entirely. Use where the caller cannot resolve a
   * template to a concrete credential itself (e.g. a flow that calls the
   * credential id directly rather than going through run-time resolution).
   */
  hideTemplates?: boolean;
  /**
   * Drop unverified credentials (no successful login, no stored session) from
   * the option list. Personas are unaffected — they resolve to a concrete,
   * presumably-verified credential per environment at run time.
   */
  onlyVerifiedCredentials?: boolean;
};

const NO_CREDENTIAL_ID = '__none__';

export const CredentialPersonaPicker: React.FC<CredentialPersonaPickerProps> = ({
  templates,
  credentials,
  propertyNameById,
  templateNameById,
  isLoading,
  selectedTemplateId,
  selectedCredentialId,
  onPick,
  label,
  ariaLabel,
  allowNoCredential = false,
  hideTemplates = false,
  onlyVerifiedCredentials = false,
}) => {
  const visibleLabel = label === undefined ? 'Credential' : label;
  const options = React.useMemo<SelectOption[]>(() => {
    const templateOptions: SelectOption[] = hideTemplates
      ? []
      : templates.map((t) => {
          const propertyName = propertyNameById.get(t.propertyId) ?? null;
          const description = [propertyName, t.description].filter(Boolean).join(' · ');
          return {
            id: t.id,
            label: t.name,
            description: description || 'Resolves per environment at run time',
            tags: [{ label: 'Persona', variant: 'secondary' as const }],
          };
        });
    const visibleCredentials = onlyVerifiedCredentials
      ? credentials.filter((c) => Boolean(c.lastLoginSucceededAt || c.storageStateS3Key))
      : credentials;
    const credentialOptions: SelectOption[] = visibleCredentials.map((c) => {
      const boundTemplateName = c.templateId ? templateNameById.get(c.templateId) : null;
      const verified = Boolean(c.lastLoginSucceededAt || c.storageStateS3Key);
      const bindingLabel = boundTemplateName
        ? `Bound to ${boundTemplateName}`
        : templates.length > 0
          ? 'Unassigned'
          : null;
      const description = [c.propertyName, bindingLabel].filter(Boolean).join(' · ');
      return {
        id: c.id,
        label: c.name,
        description: description || undefined,
        tags: verified ? [{ label: 'Verified', variant: 'success' as const }] : [],
      };
    });
    const base = [...templateOptions, ...credentialOptions];
    return allowNoCredential
      ? [{ id: NO_CREDENTIAL_ID, label: 'No credential (public)' }, ...base]
      : base;
  }, [
    templates,
    credentials,
    propertyNameById,
    templateNameById,
    allowNoCredential,
    hideTemplates,
    onlyVerifiedCredentials,
  ]);

  const selectedKey = selectedTemplateId ?? selectedCredentialId ?? (allowNoCredential ? NO_CREDENTIAL_ID : null);

  if (isLoading) {
    return (
      <Text size="sm" className="text-muted-foreground">
        Loading credentials…
      </Text>
    );
  }

  if (options.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground">
        No credentials or personas yet. Add them in Test Users.
      </Text>
    );
  }

  return (
    <Select
      key={selectedKey ?? 'empty'}
      aria-label={visibleLabel ? undefined : (ariaLabel ?? 'Credential')}
      label={visibleLabel ?? undefined}
      placeholder="Select a credential or persona"
      items={options}
      selectedKey={selectedKey}
      onSelectionChange={(key) => {
        const k = key === null ? null : String(key);
        onPick(k === NO_CREDENTIAL_ID ? null : k);
      }}
    />
  );
};
